<template>
  <div class="vehicle-card fade-in" :class="{ 'faded-in': fadedIn || fadeInRightAway }" ref="vehicleCardRef">
    <div class="image-container">
      <img
        @click="viewDetails"
        v-image-fallback="require('@/assets/vehicle-fallback.png')"
        :src="vehicle.photoURL"
        :alt="vehicleName"
        data-test="vehicleImage"
      />
    </div>
    <div class="content-container">
      <div class="row mb-1">
        <div class="col-auto">
          <div class="vehicle-details">
            {{ vehicle.year
            }}<template v-if="isUsedVehicle"
              ><span class="px-2">•</span>{{ $n(vehicle.kilometers, 'number', languageCode) }}
              {{ $t('message.credit.inventoryMatching.km') }}</template
            >
          </div>
        </div>
        <div class="col text-right">
          <div class="vehicle-price">{{ $n(vehicle.price, 'currencyDollars', languageCode) }}<sup>‡</sup></div>
        </div>
      </div>
      <div class="vehicle-name mb-1">
        {{ vehicleName }}
      </div>
      <div class="row mb-3 align-items-center">
        <div class="col-auto">
          <div class="vehicle-price-per-month">
            {{ $n(vehicleData.paymentMonthly, 'currencyDollars', languageCode)
            }}{{ $t('message.credit.vehicleCard.perMonth') }}
          </div>
        </div>
        <div class="col text-right">
          <div class="vehicle-badge">
            {{ isUsedVehicle ? $t('message.credit.vehicleCard.used') : $t('message.credit.vehicleCard.new') }}
          </div>
        </div>
      </div>
      <MobialisButton
        class="mb-1"
        type="button"
        size="btn-sm"
        variant="secondary"
        @click="goToFinanceFlow"
        v-av-analytics-cta-interaction="{
          elementText: $t('message.credit.vehicleCard.applyNow'),
          eventActionResult: 'open',
        }"
        >{{ $t('message.credit.vehicleCard.applyNow') }}</MobialisButton
      >
      <MobialisButton
        class="view-details-btn"
        type="button"
        size="btn-sm"
        variant="link"
        @click="viewDetails"
        v-av-analytics-cta-interaction="{
          elementText: $t('message.credit.vehicleCard.viewDetails'),
          eventActionResult: 'open',
        }"
        >{{ $t('message.credit.vehicleCard.viewDetails') }}</MobialisButton
      >
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import helperFunctions from '@/utils/helperFunctions';
import ImageFallback from '@/utils/directives/imageFallback';

export default {
  name: 'VehicleCard',
  props: {
    vehicleData: {
      type: Object,
      required: true,
    },
    fadeInRightAway: {
      type: Boolean,
      default: false,
    },
  },
  directives: {
    imageFallback: ImageFallback,
  },
  data() {
    return {
      fadedIn: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.onScroll);
  },
  computed: {
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
    }),
    vehicle() {
      return this.vehicleData.vehicle;
    },
    vehicleName() {
      return helperFunctions.getVehicleName(this.vehicle, ['make', 'model', 'trim']);
    },
    isUsedVehicle() {
      return this.vehicle.status === 'USED';
    },
  },
  methods: {
    onScroll() {
      if (!this.fadedIn && !this.fadeInRightAway) {
        const vehicleCardEl = this.$refs.vehicleCardRef;
        if (this.isElementInViewport(vehicleCardEl)) {
          this.fadedIn = true;
        }
      }
    },
    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) +
            rect.height / (this.isMobileScreenSize ? 1.5 : 2) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth) +
            rect.height / (this.isMobileScreenSize ? 1.5 : 2)
      );
    },
    goToFinanceFlow() {
      const payload = {
        elementInteractedWith: 'cim.applyForFinancingStart',
        vehicleId: this.vehicleData.id,
      };
      this.goToAVFinanceFlow(this.vehicleData);
      this.$store.dispatch('metrics/logModalInteraction', payload).catch(error => {
        this.$log('Error in logging interaction', error);
      });
    },
    viewDetails() {
      const payload = {
        elementInteractedWith: 'cim.viewedCIMVehicleDetails',
        vehicleId: this.vehicleData.id,
      };
      this.$router.push({
        name: `vehicle-details-${this.baseRoute}`,
        params: { id: this.vehicleData.id },
      });
      this.$store.dispatch('metrics/logModalInteraction', payload).catch(error => {
        this.$log('Error in logging interaction', error);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

.fade-in {
  opacity: 0;
  transition: 0.4s all ease-in;
  &.faded-in {
    opacity: 1;
  }
}

.vehicle-card {
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  .image-container {
    width: 100%;
    padding-bottom: 75%;
    position: relative;
    overflow: hidden;
    img {
      cursor: pointer;
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .content-container {
    padding: 10px 16px;
    .vehicle-details {
      color: #4b5863;
      font-size: 10px;
      line-height: 18px;
    }
    .vehicle-price {
      color: #4b5863;
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
    }
    .vehicle-name {
      color: #19222b;
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
    }
    .vehicle-price-per-month {
      color: $primary;
      font-size: 20px;
      font-weight: 500;
      line-height: 18px;
    }
    .vehicle-badge {
      color: $primary-dark;
      font-size: 10px;
      font-weight: 500;
      line-height: 16px;
      border-radius: 8px;
      padding: 2px 8px;
      background: $primary-light-background2;
      text-transform: uppercase;
      display: inline-block;
      margin-bottom: 3px;
    }
    .view-details-btn {
      font-size: 12px !important;
    }
  }
}
</style>
